import React, { useState } from 'react';
import { routes } from 'constants/config';
import dompurify from 'dompurify';
import request from 'utils/request_ajax';
import GroupMembershipSelector from './GroupMembershipSelector';

/* eslint-disable no-alert */

const EditGroup = ({ users, groupAssignments = [], group = false }) => {
  const [groupData, setGroupData] = useState({
    name: group?.name || '',
    ...(group ? { user_ids: group.user_ids } : users && { user_ids: [] }),
  });

  const groupAssignmentIds = groupAssignments.map(
    ({ assignable_id: assignableId }) => assignableId,
  );

  const [newMemberAssignments, setNewMemberAssignments] =
    useState(groupAssignmentIds);
  const [assignmentDueDates, setAssignmentDueDates] = useState({});
  const [newGroupMembers, setNewGroupMembers] = useState([]);

  const groupNameDirtied = groupData.name !== group.name;
  const groupMembersDirtied =
    users &&
    (newGroupMembers.length > 0 ||
      groupData.user_ids.length !== group.user_ids?.length);

  const updateGroupName = newName => {
    setGroupData({ ...groupData, name: newName });
  };

  const isExistingMember = id =>
    group.user_ids.some(memberId => memberId === id);

  const addGroupMember = id => {
    if (group && !isExistingMember(id)) {
      setNewGroupMembers([...newGroupMembers, id]);
    }

    setGroupData({
      ...groupData,
      user_ids: [...groupData.user_ids, id],
    });
  };

  const removeGroupMember = id => {
    if (group) {
      setNewGroupMembers(newGroupMembers.filter(member => member !== id));
    }

    setGroupData({
      ...groupData,
      user_ids: groupData.user_ids.filter(member => member !== id),
    });
  };

  const addMemberAssignment = id =>
    setNewMemberAssignments([...newMemberAssignments, id]);
  const removeMemberAssignment = id =>
    setNewMemberAssignments(
      newMemberAssignments.filter(assignment => assignment !== id),
    );

  const updateAssignmentDueDate = (date, id) => {
    if (date) {
      setAssignmentDueDates({
        ...assignmentDueDates,
        [id]: date,
      });
    } else {
      setAssignmentDueDates(
        Object.keys(assignmentDueDates).reduce((obj, currentId) => {
          if (currentId !== id) {
            return {
              ...obj,
              [currentId]: obj[currentId],
            };
          }

          return obj;
        }, {}),
      );
    }
  };

  const createGroup = () => {
    request
      .postJSON(routes.GROUPS, { group: groupData })
      .then(newGroupResponse => {
        window.location.href = routes.GROUP_SHOW(newGroupResponse.id);
      })
      .catch(e => {
        console.error(e);
        alert(t('groups.error'));
      });
  };

  const updateGroup = () => {
    request
      .putJSON(routes.GROUP_SHOW(group.id), {
        group: {
          ...groupData,
          ...(users && {
            new_member_assignments: {
              group_assignment_ids: newMemberAssignments,
              due_dates: assignmentDueDates,
            },
          }),
        },
      })
      .then(() => {
        window.location.href = routes.GROUP_SHOW(group.id);
      })
      .catch(e => {
        console.error(e);
        alert(t('groups.error'));
      });
  };

  return (
    <div className="panel group">
      {group && (
        <div className="back_link">
          <h1>
            <a className="decorated" href={routes.GROUP_SHOW(group.id)}>
              ← {t('groups.back')}
            </a>
          </h1>
        </div>
      )}

      <article data-behavior="expandable" className="assignable group">
        <div className="focused_form">
          <div className="focused_form_body">
            <h1>
              <input
                type="text"
                placeholder={t('groups.new_group_name_placeholder')}
                onChange={({ target: { value } }) =>
                  updateGroupName(dompurify.sanitize(value))
                }
                value={groupData.name}
              />
            </h1>

            {users ? (
              <GroupMembershipSelector
                users={users}
                isNewGroup={!group}
                groupMembers={groupData.user_ids}
                addGroupMember={addGroupMember}
                removeGroupMember={removeGroupMember}
              />
            ) : (
              t('groups.no_bulk_edit_message')
            )}
          </div>

          <footer>
            {group &&
              groupAssignments.length > 0 &&
              newGroupMembers.length > 0 && (
                <section className="group_assignments">
                  <h5>{t('groups.new_membership_assignments_header')}</h5>

                  <label htmlFor="select_all_assignments" className="checkbox">
                    <input
                      type="checkbox"
                      name="select_all_assignments"
                      checked={
                        newMemberAssignments.length === groupAssignments.length
                      }
                      onChange={({ currentTarget: { checked } }) =>
                        checked
                          ? setNewMemberAssignments(groupAssignmentIds)
                          : setNewMemberAssignments([])
                      }
                    />
                    <h5>
                      <b>{t('groups.all_group_assignments')}</b>
                    </h5>
                  </label>

                  {groupAssignments.map(
                    ({
                      assignable_id: assignableId,
                      assignable_type: assignableType,
                      title,
                    }) => {
                      return (
                        <div
                          key={`assignable${assignableId}`}
                          className="lesson"
                        >
                          <label
                            htmlFor={`assignment${assignableId}`}
                            className="checkbox"
                          >
                            <input
                              type="checkbox"
                              name={`assignment${assignableId}`}
                              checked={newMemberAssignments.some(
                                assignmentId => assignmentId === assignableId,
                              )}
                              onChange={({ currentTarget: { checked } }) =>
                                checked
                                  ? addMemberAssignment(assignableId)
                                  : removeMemberAssignment(assignableId)
                              }
                            />
                            <h5>
                              {assignableType === 'LearningPaths::Path' && (
                                <svg className="Icon">
                                  <use xlinkHref="#icons-path" />
                                </svg>
                              )}
                              {title}
                            </h5>
                          </label>

                          <span className="Label Label--dueDate">
                            <input
                              type="hidden"
                              data-behavior="due_date_field"
                              onChange={({ target: { value } }) =>
                                updateAssignmentDueDate(value, assignableId)
                              }
                            />
                            <input
                              type="text"
                              placeholder={t('groups.due_anytime')}
                              data-behavior="due_date"
                              readOnly
                            />
                            <button
                              type="button"
                              data-behavior="clear_due_date"
                              className="ButtonLink clear decorated hide"
                            >
                              {t('groups.due_date_clear')}
                            </button>
                          </span>
                        </div>
                      );
                    },
                  )}
                </section>
              )}

            <div className="actions">
              <input
                type="submit"
                value={
                  group ? t('groups.save_group') : t('groups.create_group')
                }
                className="btn"
                disabled={
                  !groupData.name ||
                  (group && !groupNameDirtied && !groupMembersDirtied)
                }
                onClick={group ? updateGroup : createGroup}
              />{' '}
              {group && (
                <span>
                  {t('groups.cancel_or')}{' '}
                  <a href={routes.GROUP_SHOW(group.id)} className="decorated">
                    {t('groups.cancel')}
                  </a>
                </span>
              )}
            </div>
          </footer>
        </div>
      </article>
    </div>
  );
};

export default props => <EditGroup {...props} />;
